import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Estimate, JobType, useDuplicateEstimate } from "@/hooks/useEstimates";
import { useCustomers } from "@/hooks/useProjects";
import { useTabNavigate } from "@/hooks/useTabNavigate";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  sourceEstimate: Estimate | null;
}

const JOB_TYPES: { value: JobType; label: string }[] = [
  { value: "flooring", label: "Flooring" },
  { value: "roofing", label: "Roofing" },
  { value: "emergency", label: "Emergency" },
  { value: "general", label: "General" },
  { value: "other", label: "Other" },
];

export const DuplicateEstimateDialog = ({ open, onOpenChange, sourceEstimate }: Props) => {
  const navigate = useTabNavigate();
  const { data: customers, isLoading: customersLoading } = useCustomers();
  const duplicate = useDuplicateEstimate();

  const [customerId, setCustomerId] = useState<string>("");
  const [projectName, setProjectName] = useState<string>("");
  const [jobType, setJobType] = useState<JobType>("general");

  // Reset state when dialog opens with a (possibly different) source
  useEffect(() => {
    if (open && sourceEstimate) {
      setCustomerId(sourceEstimate.customer_id ?? "");
      setProjectName(sourceEstimate.project_name ?? "");
      setJobType((sourceEstimate.job_type as JobType) ?? "general");
    }
  }, [open, sourceEstimate]);

  const handleSubmit = async () => {
    if (!sourceEstimate) return;
    if (!customerId) {
      toast.error("Please select a customer");
      return;
    }
    try {
      const newEstimate = await duplicate.mutateAsync({
        sourceEstimate,
        customer_id: customerId,
        project_name: projectName || null,
        job_type: jobType,
      });
      toast.success("Estimate duplicated");
      onOpenChange(false);
      navigate(`/controller/estimates/${(newEstimate as any).id}`);
    } catch (err: any) {
      toast.error(err.message || "Failed to duplicate estimate");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Duplicate Estimate</DialogTitle>
          <DialogDescription>
            Create a new Draft copy of {sourceEstimate?.estimate_number ?? "this estimate"}. You can assign it to a different customer.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="dup-customer">Customer <span className="text-destructive">*</span></Label>
            <Select value={customerId} onValueChange={setCustomerId} disabled={customersLoading}>
              <SelectTrigger id="dup-customer">
                <SelectValue placeholder={customersLoading ? "Loading…" : "Select customer"} />
              </SelectTrigger>
              <SelectContent>
                {(customers ?? []).map((c: any) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="dup-project">Project name</Label>
            <Input
              id="dup-project"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="Optional"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="dup-jobtype">Job type</Label>
            <Select value={jobType} onValueChange={(v) => setJobType(v as JobType)}>
              <SelectTrigger id="dup-jobtype"><SelectValue /></SelectTrigger>
              <SelectContent>
                {JOB_TYPES.map((j) => (
                  <SelectItem key={j.value} value={j.value}>{j.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={duplicate.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={duplicate.isPending || !customerId}>
            {duplicate.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            Create Duplicate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
